import React, { useState, useEffect } from 'react';
import { apiClient } from '../api/client';
import { useAuth } from '../contexts/AuthContext';

// ── TypeScript Interfaces ─────────────────────────────────────

type BudgetSubmissionStatus = 'Draft' | 'Submitted' | 'InReview' | 'Approved' | 'Rejected';
type LineItemStatus = 'Submitted' | 'Updated' | 'InReview' | 'Approved' | 'Other';

interface LineItemVersion {
  id: string;
  lineItemId: string;
  versionNumber: number;
  category: string;
  description: string;
  amount: number;
  justification: string | null;
  savedAt: string;
}

interface LineItemStatusLog {
  id: string;
  lineItemId: string;
  fromStatus: LineItemStatus | null;
  toStatus: LineItemStatus;
  comment: string | null;
  changedBy: string;
  changedAt: string;
}

interface LineItem {
  id: string;
  submissionId: string;
  category: string;
  description: string;
  amount: number;
  justification: string | null;
  status: LineItemStatus;
  resubmissionCount: number;
  reviewComment: string | null;
  createdAt: string;
  updatedAt: string;
  versions: LineItemVersion[];
  statusLogs: LineItemStatusLog[];
}

interface BudgetSubmission {
  id: string;
  title: string;
  description: string;
  submitterId: string;
  subcommitteeId: string;
  eventId: string | null;
  projectName: string | null;
  status: BudgetSubmissionStatus;
  totalAmount: number;
  submittedAt: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  lineItems: LineItem[];
  subcommittee?: { id: string; name: string };
}

interface SubmissionListItem {
  id: string;
  title: string;
  description: string;
  status: BudgetSubmissionStatus;
  totalAmount: number;
  submittedAt: string | null;
  subcommittee?: { id: string; name: string };
}

// ── State Machine ─────────────────────────────────────────────

const VALID_TRANSITIONS: Record<LineItemStatus, LineItemStatus[]> = {
  Submitted: ['InReview', 'Other'],
  Updated: ['InReview', 'Other'],
  InReview: ['Approved', 'Submitted', 'Other'],
  Approved: [],
  Other: ['Submitted', 'InReview'],
};

const MAX_RESUBMISSIONS = 3;

// ── Styles ────────────────────────────────────────────────────

const styles: Record<string, React.CSSProperties> = {
  page: { padding: '2rem', maxWidth: '1200px', margin: '0 auto' },
  title: { color: '#1a6b3c', fontSize: '1.75rem', margin: '0 0 1.5rem 0' },
  section: {
    marginBottom: '2rem', padding: '1.5rem', backgroundColor: '#fff',
    border: '1px solid #e0e0e0', borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
  },
  sectionTitle: {
    color: '#0f2d1f', fontSize: '1.2rem', marginBottom: '1rem',
    borderBottom: '2px solid #c9a84c', paddingBottom: '0.25rem',
  },
  btn: {
    padding: '0.5rem 1rem', backgroundColor: '#1a6b3c', color: '#fff',
    border: 'none', borderRadius: '4px', fontSize: '0.85rem',
    fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem',
  },
  btnGold: {
    padding: '0.5rem 1rem', backgroundColor: '#c9a84c', color: '#fff',
    border: 'none', borderRadius: '4px', fontSize: '0.85rem',
    fontWeight: 600, cursor: 'pointer',
  },
  btnDanger: {
    padding: '0.5rem 1rem', backgroundColor: '#d32f2f', color: '#fff',
    border: 'none', borderRadius: '4px', fontSize: '0.85rem',
    fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem',
  },
  btnSecondary: {
    padding: '0.5rem 1rem', backgroundColor: '#e0e0e0', color: '#333',
    border: 'none', borderRadius: '4px', fontSize: '0.85rem',
    fontWeight: 600, cursor: 'pointer', marginRight: '0.5rem',
  },
  btnDisabled: {
    padding: '0.5rem 1rem', backgroundColor: '#ccc', color: '#888',
    border: 'none', borderRadius: '4px', fontSize: '0.85rem',
    fontWeight: 600, cursor: 'not-allowed',
  },
  input: {
    padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px',
    fontSize: '0.9rem', boxSizing: 'border-box' as const, width: '100%',
  },
  select: {
    padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px',
    fontSize: '0.9rem', backgroundColor: '#fff',
    boxSizing: 'border-box' as const, width: '100%',
  },
  textarea: {
    padding: '0.5rem 0.75rem', border: '1px solid #ccc', borderRadius: '4px',
    fontSize: '0.9rem', boxSizing: 'border-box' as const, width: '100%',
    minHeight: '60px', resize: 'vertical' as const,
  },
  error: {
    color: '#d32f2f', backgroundColor: '#fdecea', padding: '0.75rem 1rem',
    borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem',
  },
  success: {
    color: '#2e7d32', backgroundColor: '#e8f5e9', padding: '0.75rem 1rem',
    borderRadius: '4px', marginBottom: '1rem', fontSize: '0.85rem',
  },
  tableWrapper: { overflowX: 'auto' as const },
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '0.9rem' },
  th: {
    textAlign: 'left' as const, padding: '0.75rem 0.5rem',
    borderBottom: '2px solid #e0e0e0', fontWeight: 600,
    color: '#0f2d1f', fontSize: '0.85rem',
  },
  td: {
    padding: '0.75rem 0.5rem', borderBottom: '1px solid #e0e0e0',
    fontSize: '0.85rem', color: '#333', verticalAlign: 'top' as const,
  },
  badge: {
    display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '3px',
    fontSize: '0.7rem', fontWeight: 600, color: '#fff',
  },
  loading: { textAlign: 'center' as const, padding: '3rem', color: '#666', fontSize: '1rem' },
  emptyState: { textAlign: 'center' as const, padding: '2rem', color: '#666', fontSize: '0.9rem' },
  headerRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    marginBottom: '1rem', flexWrap: 'wrap' as const, gap: '0.5rem',
  },
  headerMeta: { display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' as const },
  metaItem: { fontSize: '0.85rem', color: '#555' },
  metaLabel: { fontWeight: 600, color: '#0f2d1f' },
  accordion: {
    border: '1px solid #e0e0e0', borderRadius: '4px', marginTop: '0.5rem',
    overflow: 'hidden',
  },
  accordionHeader: {
    padding: '0.5rem 0.75rem', backgroundColor: '#f5f5f5', cursor: 'pointer',
    fontSize: '0.8rem', fontWeight: 600, color: '#0f2d1f',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  accordionBody: { padding: '0.75rem', fontSize: '0.8rem', backgroundColor: '#fafafa' },
  timeline: { paddingLeft: '1rem', borderLeft: '2px solid #c9a84c' },
  timelineItem: { marginBottom: '0.75rem', paddingLeft: '0.75rem', position: 'relative' as const },
  timelineDot: {
    position: 'absolute' as const, left: '-1.35rem', top: '0.3rem',
    width: '10px', height: '10px', borderRadius: '50%',
    backgroundColor: '#c9a84c', border: '2px solid #fff',
  },
  resubmitForm: {
    marginTop: '0.75rem', padding: '1rem', backgroundColor: '#f9f9f9',
    border: '1px solid #e0e0e0', borderRadius: '4px',
  },
  resubmitGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' },
  field: { marginBottom: '0.75rem' },
  label: { display: 'block', marginBottom: '0.25rem', fontWeight: 600, fontSize: '0.8rem', color: '#333' },
  maxResubmitMsg: {
    color: '#d32f2f', fontSize: '0.8rem', fontWeight: 600,
    padding: '0.5rem', backgroundColor: '#fdecea', borderRadius: '4px',
    marginTop: '0.5rem',
  },
};

const SUBMISSION_STATUS_COLORS: Record<BudgetSubmissionStatus, string> = {
  Draft: '#666',
  Submitted: '#1a6b3c',
  InReview: '#c9a84c',
  Approved: '#2e7d32',
  Rejected: '#d32f2f',
};

const LINE_ITEM_STATUS_COLORS: Record<LineItemStatus, string> = {
  Submitted: '#1a6b3c',
  Updated: '#1565c0',
  InReview: '#c9a84c',
  Approved: '#2e7d32',
  Other: '#666',
};

// ── Helper Functions ──────────────────────────────────────────

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
}

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
}

function formatStatusLabel(status: string): string {
  if (status === 'InReview') return 'In Review';
  return status;
}

// ── Main Component ────────────────────────────────────────────

export function BudgetReviewPage() {
  const { user } = useAuth();

  // View state: 'list' shows pending submissions, 'detail' shows a single submission
  const [view, setView] = useState<'list' | 'detail'>('list');
  const [, setSelectedSubmissionId] = useState<string | null>(null);

  // List state
  const [submissions, setSubmissions] = useState<SubmissionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Detail state
  const [submission, setSubmission] = useState<BudgetSubmission | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Review action state
  const [transitionLineItemId, setTransitionLineItemId] = useState<string | null>(null);
  const [transitionStatus, setTransitionStatus] = useState<LineItemStatus | ''>('');
  const [transitionComment, setTransitionComment] = useState('');
  const [transitionLoading, setTransitionLoading] = useState(false);

  // Resubmission state
  const [resubmitLineItemId, setResubmitLineItemId] = useState<string | null>(null);
  const [resubmitCategory, setResubmitCategory] = useState('');
  const [resubmitDescription, setResubmitDescription] = useState('');
  const [resubmitAmount, setResubmitAmount] = useState('');
  const [resubmitJustification, setResubmitJustification] = useState('');
  const [resubmitLoading, setResubmitLoading] = useState(false);

  // Accordion state for version history
  const [expandedVersions, setExpandedVersions] = useState<Set<string>>(new Set());
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());

  // Approval state
  const [approveLoading, setApproveLoading] = useState(false);

  // Determine user role capabilities
  const isOrganizer = user?.role === 'Organizer' || user?.role === 'Admin';
  // For Chair resubmission: in a real app we'd check subcommittee membership
  // Here we allow any authenticated user to resubmit (the backend enforces the real guard)
  const canResubmit = true;

  // ── Data Fetching ─────────────────────────────────────────

  useEffect(() => {
    fetchPendingSubmissions();
  }, []);

  async function fetchPendingSubmissions() {
    setLoading(true);
    setError('');
    try {
      const data = await apiClient<SubmissionListItem[]>('/budget-submissions');
      // Filter to show only Submitted and InReview for reviewers
      const pending = data.filter(
        (s) => s.status === 'Submitted' || s.status === 'InReview'
      );
      setSubmissions(pending);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load submissions.');
    } finally {
      setLoading(false);
    }
  }

  async function fetchSubmissionDetail(id: string) {
    setDetailLoading(true);
    setError('');
    try {
      const data = await apiClient<BudgetSubmission>(`/budget-submissions/${id}`);
      setSubmission(data);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to load submission details.');
    } finally {
      setDetailLoading(false);
    }
  }

  function handleSelectSubmission(id: string) {
    setSelectedSubmissionId(id);
    setView('detail');
    fetchSubmissionDetail(id);
  }

  function handleBackToList() {
    setView('list');
    setSelectedSubmissionId(null);
    setSubmission(null);
    setError('');
    setSuccessMsg('');
    fetchPendingSubmissions();
  }

  // ── Status Transition Handlers (Organizer) ────────────────

  function handleStartTransition(lineItemId: string) {
    setTransitionLineItemId(lineItemId);
    setTransitionStatus('');
    setTransitionComment('');
  }

  function handleCancelTransition() {
    setTransitionLineItemId(null);
    setTransitionStatus('');
    setTransitionComment('');
  }

  async function handleSubmitTransition() {
    if (!transitionLineItemId || !transitionStatus || !submission) return;
    setTransitionLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      await apiClient(
        `/budget-submissions/${submission.id}/line-items/${transitionLineItemId}/status`,
        {
          method: 'PATCH',
          body: { status: transitionStatus, comment: transitionComment || null },
        }
      );
      setSuccessMsg('Status updated successfully.');
      handleCancelTransition();
      // Refresh detail
      await fetchSubmissionDetail(submission.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to update status.');
    } finally {
      setTransitionLoading(false);
    }
  }

  // ── Resubmission Handlers (Chair) ─────────────────────────

  function handleStartResubmit(lineItem: LineItem) {
    setResubmitLineItemId(lineItem.id);
    setResubmitCategory(lineItem.category);
    setResubmitDescription(lineItem.description);
    setResubmitAmount(String(lineItem.amount));
    setResubmitJustification(lineItem.justification || '');
  }

  function handleCancelResubmit() {
    setResubmitLineItemId(null);
    setResubmitCategory('');
    setResubmitDescription('');
    setResubmitAmount('');
    setResubmitJustification('');
  }

  async function handleSubmitResubmit() {
    if (!resubmitLineItemId || !submission) return;
    const amount = parseFloat(resubmitAmount);
    if (!resubmitDescription.trim() || isNaN(amount) || amount <= 0) {
      setError('Description and a positive amount are required for resubmission.');
      return;
    }
    setResubmitLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      await apiClient(
        `/budget-submissions/${submission.id}/line-items/${resubmitLineItemId}/resubmit`,
        {
          method: 'POST',
          body: {
            category: resubmitCategory.trim(),
            description: resubmitDescription.trim(),
            amount,
            justification: resubmitJustification.trim() || null,
          },
        }
      );
      setSuccessMsg('Line item resubmitted successfully.');
      handleCancelResubmit();
      await fetchSubmissionDetail(submission.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to resubmit line item.');
    } finally {
      setResubmitLoading(false);
    }
  }

  // ── Approval Handler ──────────────────────────────────────

  async function handleApproveSubmission() {
    if (!submission) return;
    setApproveLoading(true);
    setError('');
    setSuccessMsg('');
    try {
      await apiClient(`/budget-submissions/${submission.id}/approve`, {
        method: 'PATCH',
      });
      setSuccessMsg('Budget submission approved! Charge accounts have been created.');
      await fetchSubmissionDetail(submission.id);
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Failed to approve submission.');
    } finally {
      setApproveLoading(false);
    }
  }

  // ── Accordion Toggles ─────────────────────────────────────

  function toggleVersions(lineItemId: string) {
    setExpandedVersions((prev) => {
      const next = new Set(prev);
      if (next.has(lineItemId)) next.delete(lineItemId);
      else next.add(lineItemId);
      return next;
    });
  }

  function toggleLogs(lineItemId: string) {
    setExpandedLogs((prev) => {
      const next = new Set(prev);
      if (next.has(lineItemId)) next.delete(lineItemId);
      else next.add(lineItemId);
      return next;
    });
  }

  // ── Check if all line items are terminal (for approve button) ─

  function allItemsTerminal(): boolean {
    if (!submission) return false;
    return submission.lineItems.every(
      (item) => item.status === 'Approved' || item.status === 'Other'
    );
  }

  // ── Render: List View ─────────────────────────────────────

  if (view === 'list') {
    return (
      <div style={styles.page}>
        <h1 style={styles.title}>Budget Review</h1>
        {error && <div style={styles.error}>{error}</div>}

        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Submissions Pending Review</h2>

          {loading ? (
            <div style={styles.loading}>Loading submissions...</div>
          ) : submissions.length === 0 ? (
            <div style={styles.emptyState}>No submissions pending review.</div>
          ) : (
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead>
                  <tr>
                    <th style={styles.th}>Title</th>
                    <th style={styles.th}>Subcommittee</th>
                    <th style={styles.th}>Status</th>
                    <th style={styles.th}>Total</th>
                    <th style={styles.th}>Submitted</th>
                    <th style={styles.th}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {submissions.map((sub) => (
                    <tr key={sub.id}>
                      <td style={styles.td}>{sub.title}</td>
                      <td style={styles.td}>{sub.subcommittee?.name || '—'}</td>
                      <td style={styles.td}>
                        <span style={{ ...styles.badge, backgroundColor: SUBMISSION_STATUS_COLORS[sub.status] }}>
                          {formatStatusLabel(sub.status)}
                        </span>
                      </td>
                      <td style={styles.td}>{formatCurrency(sub.totalAmount)}</td>
                      <td style={styles.td}>{formatDate(sub.submittedAt)}</td>
                      <td style={styles.td}>
                        <button
                          style={styles.btn}
                          onClick={() => handleSelectSubmission(sub.id)}
                        >
                          Review
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Render: Detail View ───────────────────────────────────

  if (detailLoading) {
    return (
      <div style={styles.page}>
        <div style={styles.loading}>Loading submission details...</div>
      </div>
    );
  }

  if (!submission) {
    return (
      <div style={styles.page}>
        <div style={styles.emptyState}>Submission not found.</div>
        <button style={styles.btnSecondary} onClick={handleBackToList}>← Back to List</button>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Back button */}
      <button
        style={{ ...styles.btnSecondary, marginBottom: '1rem' }}
        onClick={handleBackToList}
      >
        ← Back to List
      </button>

      {error && <div style={styles.error}>{error}</div>}
      {successMsg && <div style={styles.success}>{successMsg}</div>}

      {/* ── Submission Header (Task 7.3) ─────────────────────── */}
      <div style={styles.section}>
        <div style={styles.headerRow}>
          <h1 style={{ ...styles.title, margin: 0 }}>{submission.title}</h1>
          <span style={{ ...styles.badge, backgroundColor: SUBMISSION_STATUS_COLORS[submission.status], fontSize: '0.85rem', padding: '0.3rem 0.8rem' }}>
            {formatStatusLabel(submission.status)}
          </span>
        </div>
        <p style={{ color: '#555', fontSize: '0.9rem', margin: '0 0 1rem 0' }}>
          {submission.description}
        </p>
        <div style={styles.headerMeta}>
          <span style={styles.metaItem}>
            <span style={styles.metaLabel}>Total: </span>
            {formatCurrency(submission.totalAmount)}
          </span>
          <span style={styles.metaItem}>
            <span style={styles.metaLabel}>Created: </span>
            {formatDate(submission.createdAt)}
          </span>
          <span style={styles.metaItem}>
            <span style={styles.metaLabel}>Submitted: </span>
            {formatDate(submission.submittedAt)}
          </span>
          {submission.approvedAt && (
            <span style={styles.metaItem}>
              <span style={styles.metaLabel}>Approved: </span>
              {formatDate(submission.approvedAt)}
            </span>
          )}
          <span style={styles.metaItem}>
            <span style={styles.metaLabel}>Association: </span>
            {submission.eventId ? 'Event' : submission.projectName || '—'}
          </span>
        </div>
      </div>

      {/* ── Approve Submission Button (Task 8.1) ─────────────── */}
      {isOrganizer && submission.status !== 'Approved' && (
        <div style={{ marginBottom: '1.5rem' }}>
          <button
            style={allItemsTerminal() ? styles.btn : styles.btnDisabled}
            disabled={!allItemsTerminal() || approveLoading}
            onClick={handleApproveSubmission}
            title={allItemsTerminal() ? 'Approve this submission' : 'All line items must be Approved or Other'}
          >
            {approveLoading ? 'Approving...' : '✓ Approve Submission'}
          </button>
          {!allItemsTerminal() && (
            <span style={{ fontSize: '0.8rem', color: '#666', marginLeft: '0.5rem' }}>
              All line items must be Approved or Other before approval
            </span>
          )}
        </div>
      )}

      {/* ── Line Items Table (Task 7.3 + 8.1 + 8.2) ─────────── */}
      <div style={styles.section}>
        <h2 style={styles.sectionTitle}>Line Items</h2>
        <div style={styles.tableWrapper}>
          <table style={styles.table}>
            <thead>
              <tr>
                <th style={styles.th}>Category</th>
                <th style={styles.th}>Description</th>
                <th style={styles.th}>Amount</th>
                <th style={styles.th}>Status</th>
                <th style={styles.th}>Resubmissions</th>
                <th style={styles.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {submission.lineItems.map((item) => (
                <React.Fragment key={item.id}>
                  <tr>
                    <td style={styles.td}>{item.category}</td>
                    <td style={styles.td}>
                      {item.description}
                      {item.justification && (
                        <div style={{ fontSize: '0.75rem', color: '#777', marginTop: '0.25rem' }}>
                          <em>Justification: {item.justification}</em>
                        </div>
                      )}
                    </td>
                    <td style={styles.td}>{formatCurrency(item.amount)}</td>
                    <td style={styles.td}>
                      <span style={{ ...styles.badge, backgroundColor: LINE_ITEM_STATUS_COLORS[item.status] }}>
                        {formatStatusLabel(item.status)}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <span style={{ fontWeight: 600, color: item.resubmissionCount >= MAX_RESUBMISSIONS ? '#d32f2f' : '#333' }}>
                        {item.resubmissionCount}/{MAX_RESUBMISSIONS}
                      </span>
                    </td>
                    <td style={styles.td}>
                      {/* Organizer: Status transition controls (Task 8.1) */}
                      {isOrganizer && VALID_TRANSITIONS[item.status].length > 0 && (
                        <button
                          style={styles.btnGold}
                          onClick={() => handleStartTransition(item.id)}
                          disabled={transitionLineItemId === item.id}
                        >
                          Change Status
                        </button>
                      )}

                      {/* Chair: Resubmit button (Task 8.2) */}
                      {canResubmit && item.status !== 'Approved' && item.resubmissionCount < MAX_RESUBMISSIONS && (
                        <button
                          style={{ ...styles.btn, marginLeft: '0.25rem' }}
                          onClick={() => handleStartResubmit(item)}
                          disabled={resubmitLineItemId === item.id}
                        >
                          Resubmit
                        </button>
                      )}

                      {/* Maximum resubmissions reached message (Task 8.2) */}
                      {item.status !== 'Approved' && item.resubmissionCount >= MAX_RESUBMISSIONS && (
                        <div style={styles.maxResubmitMsg}>Maximum resubmissions reached</div>
                      )}

                      {/* Version history toggle (Task 7.3) */}
                      <div style={{ marginTop: '0.5rem', display: 'flex', gap: '0.5rem' }}>
                        {item.versions && item.versions.length > 0 && (
                          <button
                            style={{ ...styles.btnSecondary, fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => toggleVersions(item.id)}
                          >
                            {expandedVersions.has(item.id) ? '▾ Versions' : '▸ Versions'} ({item.versions.length})
                          </button>
                        )}
                        {item.statusLogs && item.statusLogs.length > 0 && (
                          <button
                            style={{ ...styles.btnSecondary, fontSize: '0.75rem', padding: '0.25rem 0.5rem' }}
                            onClick={() => toggleLogs(item.id)}
                          >
                            {expandedLogs.has(item.id) ? '▾ History' : '▸ History'} ({item.statusLogs.length})
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Status Transition Form (Task 8.1) */}
                  {transitionLineItemId === item.id && (
                    <tr>
                      <td colSpan={6} style={{ ...styles.td, backgroundColor: '#f9f9f9' }}>
                        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                          <div style={{ minWidth: '150px' }}>
                            <label style={styles.label}>New Status</label>
                            <select
                              style={styles.select}
                              value={transitionStatus}
                              onChange={(e) => setTransitionStatus(e.target.value as LineItemStatus)}
                              aria-label="Select new status"
                            >
                              <option value="">Select status...</option>
                              {VALID_TRANSITIONS[item.status].map((s) => (
                                <option key={s} value={s}>{formatStatusLabel(s)}</option>
                              ))}
                            </select>
                          </div>
                          <div style={{ flex: 1, minWidth: '200px' }}>
                            <label style={styles.label}>Comment (optional)</label>
                            <input
                              style={styles.input}
                              type="text"
                              value={transitionComment}
                              onChange={(e) => setTransitionComment(e.target.value)}
                              placeholder="Reviewer feedback..."
                              aria-label="Transition comment"
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              style={styles.btn}
                              onClick={handleSubmitTransition}
                              disabled={!transitionStatus || transitionLoading}
                            >
                              {transitionLoading ? 'Saving...' : 'Apply'}
                            </button>
                            <button style={styles.btnSecondary} onClick={handleCancelTransition}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Resubmission Form (Task 8.2) */}
                  {resubmitLineItemId === item.id && (
                    <tr>
                      <td colSpan={6} style={{ ...styles.td, padding: 0 }}>
                        <div style={styles.resubmitForm}>
                          <h4 style={{ margin: '0 0 0.75rem 0', fontSize: '0.9rem', color: '#0f2d1f' }}>
                            Resubmit Line Item (Attempt {item.resubmissionCount + 1}/{MAX_RESUBMISSIONS})
                          </h4>
                          <div style={styles.resubmitGrid}>
                            <div style={styles.field}>
                              <label style={styles.label}>Category</label>
                              <input
                                style={styles.input}
                                type="text"
                                value={resubmitCategory}
                                onChange={(e) => setResubmitCategory(e.target.value)}
                                maxLength={100}
                                aria-label="Resubmit category"
                              />
                            </div>
                            <div style={styles.field}>
                              <label style={styles.label}>Amount ($)</label>
                              <input
                                style={styles.input}
                                type="number"
                                step="0.01"
                                min="0.01"
                                value={resubmitAmount}
                                onChange={(e) => setResubmitAmount(e.target.value)}
                                aria-label="Resubmit amount"
                              />
                            </div>
                          </div>
                          <div style={styles.field}>
                            <label style={styles.label}>Description *</label>
                            <textarea
                              style={styles.textarea}
                              value={resubmitDescription}
                              onChange={(e) => setResubmitDescription(e.target.value)}
                              maxLength={500}
                              aria-label="Resubmit description"
                            />
                          </div>
                          <div style={styles.field}>
                            <label style={styles.label}>Justification</label>
                            <textarea
                              style={styles.textarea}
                              value={resubmitJustification}
                              onChange={(e) => setResubmitJustification(e.target.value)}
                              aria-label="Resubmit justification"
                            />
                          </div>
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button
                              style={styles.btn}
                              onClick={handleSubmitResubmit}
                              disabled={resubmitLoading}
                            >
                              {resubmitLoading ? 'Submitting...' : 'Resubmit'}
                            </button>
                            <button style={styles.btnSecondary} onClick={handleCancelResubmit}>
                              Cancel
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Version History Accordion (Task 7.3) */}
                  {expandedVersions.has(item.id) && item.versions && item.versions.length > 0 && (
                    <tr>
                      <td colSpan={6} style={{ ...styles.td, padding: '0.5rem' }}>
                        <div style={styles.accordion}>
                          <div style={styles.accordionHeader}>
                            <span>Version History</span>
                            <span style={{ fontSize: '0.7rem', color: '#666' }}>
                              {item.versions.length} version{item.versions.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div style={styles.accordionBody}>
                            {[...item.versions]
                              .sort((a, b) => b.versionNumber - a.versionNumber)
                              .map((version) => (
                                <div
                                  key={version.id}
                                  style={{
                                    padding: '0.5rem',
                                    borderBottom: '1px solid #eee',
                                    marginBottom: '0.5rem',
                                  }}
                                >
                                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                                    <span style={{ fontWeight: 600, color: '#0f2d1f' }}>
                                      Version {version.versionNumber}
                                    </span>
                                    <span style={{ fontSize: '0.75rem', color: '#888' }}>
                                      {formatDateTime(version.savedAt)}
                                    </span>
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: '#555' }}>
                                    <strong>{version.category}</strong> — {version.description}
                                  </div>
                                  <div style={{ fontSize: '0.8rem', color: '#1a6b3c', fontWeight: 600 }}>
                                    {formatCurrency(version.amount)}
                                  </div>
                                  {version.justification && (
                                    <div style={{ fontSize: '0.75rem', color: '#777', fontStyle: 'italic' }}>
                                      {version.justification}
                                    </div>
                                  )}
                                </div>
                              ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {/* Status Log Timeline (Task 7.3) */}
                  {expandedLogs.has(item.id) && item.statusLogs && item.statusLogs.length > 0 && (
                    <tr>
                      <td colSpan={6} style={{ ...styles.td, padding: '0.5rem' }}>
                        <div style={styles.accordion}>
                          <div style={styles.accordionHeader}>
                            <span>Status History</span>
                            <span style={{ fontSize: '0.7rem', color: '#666' }}>
                              {item.statusLogs.length} change{item.statusLogs.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div style={styles.accordionBody}>
                            <div style={styles.timeline}>
                              {[...item.statusLogs]
                                .sort((a, b) => new Date(b.changedAt).getTime() - new Date(a.changedAt).getTime())
                                .map((log) => (
                                  <div key={log.id} style={styles.timelineItem}>
                                    <div style={styles.timelineDot} />
                                    <div style={{ fontSize: '0.8rem' }}>
                                      <span style={{ fontWeight: 600 }}>
                                        {log.fromStatus ? formatStatusLabel(log.fromStatus) : 'Initial'}
                                      </span>
                                      {' → '}
                                      <span style={{ fontWeight: 600, color: LINE_ITEM_STATUS_COLORS[log.toStatus] }}>
                                        {formatStatusLabel(log.toStatus)}
                                      </span>
                                    </div>
                                    {log.comment && (
                                      <div style={{ fontSize: '0.75rem', color: '#555', fontStyle: 'italic', marginTop: '0.15rem' }}>
                                        "{log.comment}"
                                      </div>
                                    )}
                                    <div style={{ fontSize: '0.7rem', color: '#999', marginTop: '0.15rem' }}>
                                      {formatDateTime(log.changedAt)}
                                    </div>
                                  </div>
                                ))}
                            </div>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
